/**
 * The audit log, as the collector reads it: every act recorded against what
 * they can see, newest first, narrowed by who did it and what kind of act.
 *
 * Pure. A feed and the reader's id go in; rows, chips and captions come out.
 * The vocabulary — which verbs a chip admits, what a pill says — is
 * `activity.constants.ts`, and the fetch, the selected filters and the
 * loading/error split are `useActivityController.ts`. This file owns only the
 * decisions between, and takes no clock: an audit line carries an absolute
 * date or is dropped, so nothing here needs `now`.
 *
 * Two rules a reader would otherwise get wrong: a `provenance.added` row is a
 * transfer only when its kind moves title, and an unknown kind falls to
 * History, never to Transfer — see `bucketOf`. Saying less is allowed; saying
 * that ownership changed when it may not have is not.
 */

import { isActivityType, movesTitle } from "@ondo/shared";
import type { ActivityEntry, CollectionSummary } from "@ondo/shared";

import { describeAct } from "../lib/activityCopy.ts";
import { initialsOf } from "../lib/names.ts";
import type { TagTone } from "../lib/Tag.tsx";
import { activityHref } from "../routes/routes.model.ts";
import {
  ACTION_FILTERS,
  ACTIVITY_COPY,
  ACTIVITY_PAGE,
  ACTIVITY_TAGS,
  ALL,
  EMPTY_COPY,
  TITLE_TAG,
} from "./activity.constants.ts";

/** The USER cell. Null when the feed recorded no actor. */
export interface WhoCell {
  initials: string;
  /** The display name, or "You" for the reader's own act. */
  name: string;
  /**
   * The reference design puts a ROLE under the name. ONDO has none to give:
   * `ActivityEntry.actor` carries an id and a name and nothing else, and there
   * is no endpoint that resolves a principal to a role — `share_collection`
   * makes "no such principal" and "not permitted" the same answer on purpose.
   * An id is what the registry actually recorded.
   */
  ondoId: string;
}

export interface AuditRow {
  id: string;
  who: WhoCell | null;
  /** First line: "Condition report verified". */
  action: string;
  /** Second line: the work or collection's name. Null when it had none yet. */
  subject: string | null;
  glyph: string;
  tag: { label: string; tone: TagTone };
  /** "2 Mar 2026 · 09:14" — absolute, paired with `isoTime` for `<time>`. */
  when: string;
  isoTime: string;
  /** Which action chip admits this row. */
  bucket: string;
  /** Which user chip admits it. Empty when nobody was recorded. */
  actorId: string;
  /** Where the row goes, or null for a collection, which has no screen. */
  href: string | null;
}

export interface FilterChip {
  id: string;
  label: string;
  glyph: string;
  count: number;
  pressed: boolean;
  /** Null when the chip works; the reason when it can never match. */
  absent: string | null;
}

export interface Filter {
  user: string;
  action: string;
}

/**
 * Which action chip a row belongs to.
 *
 * `movesTitle` is what splits `provenance.added` in two. A sale or an auction IS
 * the chain of title; an exhibition is not, and a reader auditing ownership
 * should not have to read past eleven condition reports to find it.
 *
 * A NULL KIND FALLS TO HISTORY AND NEVER TO TRANSFER, which is the one branch
 * here worth arguing for. Null means three different things — a row written
 * before kinds existed, one written by a build that knows a kind this one does
 * not, or an entry the recording client could not read back. Bucketing an
 * unknown kind as a change of ownership would have the interface assert that
 * title moved. "History" says less; "Transfer" would risk saying something
 * false.
 *
 * AND `digest.missed` FALLS THROUGH TOO — OND-205 — so it is readable under
 * "All" and under no other chip. That is deliberate rather than an omission:
 * every chip in `ACTION_FILTERS` names a kind of act on a RECORD, and a summary
 * that did not arrive is not one. Giving it a chip of its own would put a
 * delivery fault on the same axis as registering, granting and verifying, and
 * the honest answer is that it belongs on none of them. The fallback bucket is
 * the one `provenance.added` already lands in for every kind that does not move
 * title, so the behaviour is not new.
 */
export function bucketOf(entry: ActivityEntry): string {
  for (const filter of ACTION_FILTERS) {
    if (filter.id === ALL) continue;
    if (!filter.verbs.includes(entry.type)) continue;

    // A verification ON a sale is a verification, not a change of ownership:
    // the bucket is the act, not the link it was about.
    if (filter.titleMovingOnly) {
      return entry.historyKind !== null && movesTitle(entry.historyKind)
        ? filter.id
        : "history";
    }

    return filter.id;
  }

  return "history";
}

/** The TYPE pill, with the same title-moving split `bucketOf` makes. */
export function tagOf(entry: ActivityEntry): { label: string; tone: TagTone } {
  if (
    entry.type === "provenance.added" &&
    entry.historyKind !== null &&
    movesTitle(entry.historyKind)
  ) {
    return { label: TITLE_TAG.label, tone: TITLE_TAG.tone };
  }

  return ACTIVITY_TAGS[entry.type];
}

/**
 * Every readable act, newest first.
 *
 * Deduped by activity id: one activity is referenced by several feeds, so the
 * same line can be reached twice if a caller ever merges reads.
 *
 * Two kinds of row are dropped rather than guessed at, and both are the recorded
 * history being wider than this build — an act it has no name for, and a
 * timestamp it cannot read. A kind it does not recognise is NOT dropped: that
 * one reads under the general sentence, because dropping it would hide a real
 * thing that happened where saying less merely says less.
 */
export function toRows(input: {
  entries: readonly ActivityEntry[];
  /** Names by id, for the label a share deliberately does not carry. */
  collections: readonly CollectionSummary[];
  /** '/collector/me/work' — where a row about a work goes. */
  workBase: string;
  /** The reader's own ONDO id, so their acts read "You". Null while loading. */
  viewerOndoId: string | null;
}): AuditRow[] {
  const names = new Map(input.collections.map((one) => [one.id, one.name]));
  const unique = new Map<string, ActivityEntry>();
  for (const entry of input.entries) {
    if (!unique.has(entry.id)) unique.set(entry.id, entry);
  }

  return [...unique.values()]
    .flatMap((entry) => {
      if (!isActivityType(entry.type)) return [];

      const act = describeAct(entry.type, entry.historyKind);
      if (!act) return [];

      const stamp = stampOf(entry.createdAt);
      // A row whose date cannot be read is not auditable. Dropping it beats
      // dating it "just now" and being wrong by a decade.
      if (!stamp) return [];

      return [
        {
          id: entry.id,
          who: whoOf(entry, input.viewerOndoId),
          action: act.title,
          subject: labelOf(entry, names),
          glyph: act.glyph,
          tag: tagOf(entry),
          when: stamp,
          isoTime: entry.createdAt,
          bucket: bucketOf(entry),
          actorId: entry.actor?.ondoId ?? "",
          href: activityHref(entry, input.workBase),
          at: Date.parse(entry.createdAt),
        },
      ];
    })
    .sort((a, b) => b.at - a.at)
    .map(({ at: _at, ...row }) => row);
}

/** The rows both axes admit. `ALL` on an axis is every row on that axis. */
export function inFilter(rows: readonly AuditRow[], filter: Filter): AuditRow[] {
  return rows.filter(
    (row) =>
      (filter.user === ALL || row.actorId === filter.user) &&
      (filter.action === ALL || row.bucket === filter.action),
  );
}

/**
 * The USER chips, derived from the actors actually present.
 *
 * Derived and not fetched, because there is no endpoint that resolves a
 * principal — a filter offering a name absent from the list would be this screen
 * leaking a directory.
 *
 * KEYED ON THE ONDO ID, NOT THE NAME. `displayName` is a snapshot taken when the
 * activity was written and carries no uniqueness, so two principals sharing one
 * get two chips rather than being silently merged.
 *
 * Counts are CROSS-FILTERED: each chip counts what would show if it alone were
 * pressed, given the other axis. A chip reading 0 is telling the truth about
 * where pressing it lands.
 */
export function userChips(rows: readonly AuditRow[], filter: Filter): FilterChip[] {
  const onAxis = rows.filter(
    (row) => filter.action === ALL || row.bucket === filter.action,
  );

  const seen = new Map<string, { name: string; count: number }>();
  for (const row of onAxis) {
    if (!row.who) continue;

    const held = seen.get(row.actorId);
    if (held) held.count += 1;
    else seen.set(row.actorId, { name: row.who.name, count: 1 });
  }

  const named = [...seen]
    .map(([id, one]) => ({
      id,
      label: one.name,
      glyph: "",
      count: one.count,
      pressed: filter.user === id,
      absent: null,
    }))
    // The reader first, then by how much each did, then by name so the order is
    // stable when two are level.
    .sort((a, b) => {
      const self = Number(b.label === ACTIVITY_COPY.self) - Number(a.label === ACTIVITY_COPY.self);
      return self || b.count - a.count || a.label.localeCompare(b.label);
    });

  return [
    {
      id: ALL,
      label: "All users",
      glyph: "",
      count: onAxis.length,
      pressed: filter.user === ALL,
      absent: null,
    },
    ...named,
  ];
}

/** The ACTION TYPE chips. Fixed buckets, counted the same cross-filtered way. */
export function actionChips(rows: readonly AuditRow[], filter: Filter): FilterChip[] {
  const onAxis = rows.filter((row) => filter.user === ALL || row.actorId === filter.user);

  return ACTION_FILTERS.map((one) => ({
    id: one.id,
    label: one.label,
    glyph: one.glyph,
    count:
      one.id === ALL ? onAxis.length : onAxis.filter((row) => row.bucket === one.id).length,
    pressed: filter.action === one.id,
    absent: one.absent,
  }));
}

/**
 * The selection, made real.
 *
 * A user filter can outlive the actor it names — a feed reloaded, a grant
 * narrowed — and falling back to All shows the reader a list rather than a blank
 * they cannot explain. An action filter cannot go stale the same way: its
 * buckets are fixed, and a disabled one legitimately matches nothing.
 */
export function resolveFilter(rows: readonly AuditRow[], wanted: Filter): Filter {
  const present = wanted.user === ALL || rows.some((row) => row.actorId === wanted.user);

  return {
    user: present ? wanted.user : ALL,
    action: ACTION_FILTERS.some((one) => one.id === wanted.action) ? wanted.action : ALL,
  };
}

/** "No events", "1 event", "12 events". Counts what is SHOWN, not what loaded. */
export function caption(shown: number): string {
  if (shown === 0) return "No events";
  return shown === 1 ? "1 event" : `${shown} events`;
}

/** Which emptiness this is, or null when there is something to show. */
export function emptyFor(input: {
  total: number;
  shown: number;
}): { lead: string; detail: string } | null {
  if (input.shown > 0) return null;
  return input.total === 0 ? EMPTY_COPY.never : EMPTY_COPY.filtered;
}

/**
 * What the list cannot promise.
 *
 * A full page means the server's ceiling was reached and there is no cursor to
 * follow. A screen calling itself an audit log and staying quiet about that
 * would be making its strongest claim and its least supportable one.
 */
export function noticeFor(loaded: number): string | null {
  return loaded >= ACTIVITY_PAGE ? ACTIVITY_COPY.capped : null;
}

export function describeError(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : "Your activity could not be read.";
}

/** "2 Mar 2026 · 09:14". Null for a timestamp that cannot be read. */
/**
 * "2 Mar 2026 · 09:14", never "3 wks ago".
 *
 * EXPORTED SINCE OND-68, which added a second tab to this screen. The access
 * trail's timestamps and the activity feed's are the same shape because they
 * are the same function: two definitions would be one rounding decision away
 * from a reader seeing one event stamped two ways on two tabs of one audit log.
 */
export function stampOf(iso: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const day = at.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return `${day} · ${time}`;
}

function whoOf(entry: ActivityEntry, viewerOndoId: string | null): WhoCell | null {
  if (!entry.actor) return null;

  const self = viewerOndoId !== null && entry.actor.ondoId === viewerOndoId;
  const name = self ? ACTIVITY_COPY.self : entry.actor.displayName;

  return { initials: initialsOf(entry.actor.displayName), name, ondoId: entry.actor.ondoId };
}

/**
 * The feed's own snapshot label first, then the collection's name.
 *
 * An empty string on the wire is treated as no label: the type says
 * `string | null`, and a blank second line beats printing "Untitled" at a work
 * whose owner has not named it yet.
 */
function labelOf(entry: ActivityEntry, names: Map<string, string>): string | null {
  const label = entry.subject.label?.trim();
  if (label) return label;

  if (entry.subject.kind === "collection") return names.get(entry.subject.id) ?? null;
  return null;
}

